// src/auth/tenantPurged.js
//
// A PURGED workspace (backend 554ad59) answers 410 `TENANT_PURGED` on every route that names it — superadmins
// included, and before any check of the JWT claims, so a member whose token still lists it gets 410, never 403.
// It is gone for everyone, for ever (a purged id is never reused) — unlike 403 TENANT_ACCESS_DENIED ("you have no
// access"). Contract: <BACKEND_FOLDER>/docs/frontend/2026-10-09-tenant-purged-handoff.md and
// <BACKEND_FOLDER>/docs/api-server/endpoints.md#purge.
//
// This module owns what the FE does about it, so apiFetch and the raw-fetch clients share one path:
//   • which workspace was purged: the 410 body names it in `tenant_id` (`readTenantPurged`) — the only answer when
//     the request named none and the server's default replied, and the right one for a cached 410 replayed on
//     another workspace's request. An older backend's body has no id (its `message` is English prose, never parsed):
//     then the workspace the request named (`tenantNamedByRequest`) — a /user-config/tenants/{id}/ path (those two
//     routes read the path id only), then a non-empty `?tenant=`, then the `X-Tenant-Id` header actually sent, the
//     backend's own order;
//   • the local drop (`dropPurgedTenant`): out of `auth_tenants`, out of `auth_tenant_id` if it was the active one,
//     and INTO `auth_purged_tenants` — boot takes `tenants` from GET /auth/me, which replays the token's claims and
//     would bring the workspace back on every reload until the next sign-in. A new session (saveAuth) and a
//     sign-out (clearAuthStorage) clear that list: /auth/login builds claims from the DB, where the row is gone;
//   • the signal to AuthContext (`notifyTenantPurged`), which updates the state and navigates. Storage is written
//     FIRST and synchronously, so of several calls that 410 together only the first reports `wasActive: true`.
// ⛔ A 410 is never permissions drift: nothing here calls noteRefusalIfDrift.
// ⚠️ Kept out of apiFetch.js on purpose: many suites jest.mock that module with a factory exposing only `apiFetch`,
// so a helper exported from there would be undefined inside them.

export const PURGED_TENANTS_KEY = 'auth_purged_tenants';
export const TENANT_PURGED = 'TENANT_PURGED';
// One-shot flag for the superadmin console banner (read by admin/AdminLayout.jsx on every navigation).
export const SA_PURGED_BANNER_KEY = 'sa_purged_banner';

// The superadmin console pages remember the last tenant they showed, per user; a purged id left there would be
// re-sent on every visit. Values are raw tenant ids.
const ADMIN_LAST_TENANT_PREFIXES = [
  'kb_last_tenant:',
  'personas_last_tenant:',
  'ctxconfig_last_tenant:',
  'runs_last_tenant:',
  'admin_overview_tenant:',
];

const USER_CONFIG_TENANT = /\/user-config\/tenants\/([^/?#]+)\//;

let _onTenantPurged = null;

/** AuthContext registers the handler (through apiFetch's registerAuthCallbacks). `null` unregisters it. */
export function setTenantPurgedHandler(fn) {
  _onTenantPurged = typeof fn === 'function' ? fn : null;
}

export function loadPurgedTenants() {
  try {
    const raw = JSON.parse(localStorage.getItem(PURGED_TENANTS_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((id) => typeof id === 'string' && id !== '') : [];
  } catch {
    return [];
  }
}

/** `tenants` without the purged ones — a new array; a non-array is `[]`. */
export function withoutPurged(tenants, purged = loadPurgedTenants()) {
  if (!Array.isArray(tenants)) return [];
  return tenants.filter((t) => !purged.includes(t?.tenant_id));
}

function readStoredTenants() {
  try {
    const raw = JSON.parse(localStorage.getItem('auth_tenants') || 'null');
    return Array.isArray(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** Remove the workspace from this browser's session. Returns whether it was the stored active one. */
export function dropPurgedTenant(tenantId) {
  const purged = loadPurgedTenants();
  if (!purged.includes(tenantId)) {
    localStorage.setItem(PURGED_TENANTS_KEY, JSON.stringify([...purged, tenantId]));
  }
  const stored = readStoredTenants();
  if (stored) {
    localStorage.setItem('auth_tenants', JSON.stringify(stored.filter((t) => t?.tenant_id !== tenantId)));
  }
  const wasActive = localStorage.getItem('auth_tenant_id') === tenantId;
  if (wasActive) localStorage.removeItem('auth_tenant_id');
  return { wasActive };
}

/** The workspace a request named, in the backend's own order; null when it named none. */
export function tenantNamedByRequest(url, headers = {}) {
  let parsed;
  try {
    parsed = new URL(url, window.location.origin);
  } catch {
    return null;
  }
  const pathMatch = parsed.pathname.match(USER_CONFIG_TENANT);
  if (pathMatch) return decodeURIComponent(pathMatch[1]);
  const query = parsed.searchParams.get('tenant');
  if (query) return query;
  return headers?.['X-Tenant-Id'] || null;
}

/**
 * For a 410 whose body says TENANT_PURGED: `{ tenantId }` — the body's `tenant_id`, or null when it names none (an
 * older backend). Anything else: null. Never consumes the caller's body.
 */
export async function readTenantPurged(res) {
  if (!res || res.status !== 410) return null;
  try {
    const source = typeof res.clone === 'function' ? res.clone() : res;
    const body = await source.json();
    if (body?.error_code !== TENANT_PURGED) return null;
    const named = body.tenant_id;
    return { tenantId: typeof named === 'string' && named !== '' ? named : null };
  } catch {
    return null;
  }
}

/** True for a 410 whose body says TENANT_PURGED. Never consumes the caller's body. */
export async function isTenantPurgedResponse(res) {
  return (await readTenantPurged(res)) !== null;
}

/** What apiFetch throws: its message is the code — a support handle, never server prose. */
export function tenantPurgedError() {
  const err = new Error(TENANT_PURGED);
  err.code = TENANT_PURGED;
  err.status = 410;
  return err;
}

/** Drop the workspace locally, then tell AuthContext (state + navigation). No id → nothing to drop. */
export function notifyTenantPurged(tenantId) {
  if (!tenantId) return;
  const { wasActive } = dropPurgedTenant(tenantId);
  try {
    _onTenantPurged?.(tenantId, { wasActive });
  } catch {
    /* a failing navigation must not replace the caller's own error */
  }
}

/**
 * For raw-fetch callers: if `res` is a TENANT_PURGED 410, report the workspace its body names — else `tenantId`, the
 * one the request named — and return true.
 */
export async function reportIfTenantPurged(res, tenantId) {
  const purged = await readTenantPurged(res);
  if (!purged) return false;
  notifyTenantPurged(purged.tenantId || tenantId);
  return true;
}

/** Superadmin console: forget the stored "last tenant" of each admin page that still points at `tenantId`. */
export function clearAdminLastTenantKeys(tenantId) {
  try {
    const stale = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (
        key
        && ADMIN_LAST_TENANT_PREFIXES.some((prefix) => key.startsWith(prefix))
        && localStorage.getItem(key) === tenantId
      ) {
        stale.push(key);
      }
    }
    stale.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage unavailable (private mode): nothing to clear */
  }
}
